import { getRailsClient } from "sj-ui-toolkit/src/client";
import { getContentClient } from "sj-ui-toolkit/src/client/svcContent";
import type { CatalogTagOption } from "sj-ui-toolkit/src/client/svcContent";
import { normalizeTagCategories } from "sj-ui-toolkit/src/utils/catalogTags";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Lesson {
  id: string | number;
  name: string;
  description: string;
  duration: number;
  contentType?: string;
  durationNumber?: number;
  points?: number;
  lessonKey?: string;
  status?: string;
}

export interface CustomResource {
  id: string;
  name: string;
  description: string;
  url: string;
  createdAt: string;
}

export interface LessonFilters {
  tags?: string[];
  cwe_tags?: string[];
  difficulty?: string[];
  lesson_type?: string[];
  release?: string[];
  constraint?: string;
  hide_hands_on?: boolean;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

// svc-content IDs differ from the Rails lesson_type enum values
export const CONTENT_TYPE_TO_LESSON_TYPE: Record<string, string> = {
  VideoLesson: "only_video",
  VideoWithCodingExercise: "with_experiments",
  HandsOnLesson: "hackedu",
  CodingChallenge: "code_challenge",
  CtfChallenge: "hacking_challenge",
  AppLab: "app_lab",
};

export const loadLessonsAsync = async (filters: LessonFilters = {}): Promise<Lesson[]> => {
  const API = `/api/internal_api/v1/admin/lessons`;
  const params: Record<string, unknown> = {};

  if (filters.tags && filters.tags.length > 0) {
    params["tags"] = filters.tags.flatMap(t => t.split(","));
  }
  if (filters.cwe_tags && filters.cwe_tags.length > 0) {
    params["cwe_tags"] = filters.cwe_tags;
  }
  if (filters.difficulty && filters.difficulty.length > 0) {
    params["difficulty"] = filters.difficulty;
  }
  if (filters.lesson_type && filters.lesson_type.length > 0) {
    params["lesson_type"] = filters.lesson_type.map(t => CONTENT_TYPE_TO_LESSON_TYPE[t] ?? t);
  }
  if (filters.release && filters.release.length > 0) {
    params["release"] = filters.release;
  }
  if (filters.constraint) {
    params["constraint"] = filters.constraint;
  }
  if (filters.hide_hands_on !== undefined) {
    params["hide_hands_on"] = filters.hide_hands_on;
  }

  const { data } = await getRailsClient().get<{ lessons: Lesson[] }>(API, { params });
  return data?.lessons ?? [];
};

export const loadSvcContentTagsAsync = async (
  dynamicCatalogTags: boolean
): Promise<Record<string, CatalogTagOption[]>> => {
  const selectedTenant = getCurrentSelectedTenant();
  const raw =
    dynamicCatalogTags && selectedTenant
      ? await getContentClient().getTagsByTenant(selectedTenant)
      : await getContentClient().getTags();
  return normalizeTagCategories(raw);
};

export const loadResourcesForLessonAsync = async (
  lessonId: string
): Promise<{ customResources: CustomResource[] }> => {
  const API = `/api/internal_api/v1/admin/resources`;
  const { data } = await getRailsClient().get<{ customResources: CustomResource[] }>(API, {
    params: { id: lessonId },
  });
  return data ?? { customResources: [] };
};

export const createResourceAsync = async (resourceData: {
  name: string;
  description: string;
  url: string;
  lesson_group_id: string;
}): Promise<CustomResource> => {
  const API = `/api/internal_api/v1/admin/resources`;
  const { data } = await getRailsClient().post<CustomResource>(API, resourceData);
  return data;
};

export const updateResourceAsync = async (
  id: string,
  resourceData: { name: string; description: string; url: string }
): Promise<CustomResource> => {
  const API = `/api/internal_api/v1/admin/resources/${id}`;
  const { data } = await getRailsClient().put<CustomResource>(API, resourceData);
  return data;
};

export const deleteResourcesBulkAsync = async (ids: string[]): Promise<void> => {
  const API = `/api/internal_api/v1/admin/resources/destroy_many`;
  await getRailsClient().delete(API, { params: { ids } });
};
